import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { SortDir } from '../SortableHeader';

/**
 * Shared right-click context menu for a data-grid COLUMN HEADER (AppDataGrid,
 * "AppDataGrid global grid features" task).  Opened by right-clicking a header
 * cell; positioned at the cursor and clamped inside the viewport — the SAME
 * transient-popup rules as the cell menu (<CellContextMenu>) and the column
 * filter popups (§18.6): close on outside-click / Escape / scroll.
 *
 * It reuses the shared `.cell-ctx-*` menu styling (theme/boStandard.css) so a
 * header menu looks identical to the cell menu; header-specific bits (active
 * tick, disabled item, section divider) are the `.adg-hctx-*` classes in
 * appGrid.css.
 *
 * Feature roll-out (per the client's list):
 *   1. Sort — Sort Ascending · Sort Descending · Clear Sort   ✓
 *   2. Hide Column                                             ✓
 *   3. Show All Columns                                        ✓
 *   4. Column Chooser                                          ✓
 * Drag-reorder + resize are direct header interactions, not menu items.
 *
 * The column-management items (Hide / Show all / Chooser) render ONLY when the
 * grid supplies their handlers (<AppDataGrid>, which owns a column model). A
 * hand-rolled `.dv-tbl` / reporting table using useHeaderMenu omits them, so it
 * shows just the Sort section — those grids have no column model to hide/reorder.
 */
export type HeaderMenuColumn = {
  field: string;
  /** Plain-text header label (shown as the menu title). */
  label: string;
  /** Whether this column can be sorted (Sort items are disabled when false). */
  sortable: boolean;
};

export type HeaderContextMenuProps = {
  column: HeaderMenuColumn;
  /** The grid's current sort state, so the active direction shows a tick and
   *  Clear Sort disables when this column is not the sorted one. */
  sort: { key: string | null; dir: SortDir };
  /** Viewport coordinates of the right-click (e.clientX / e.clientY). */
  position: { x: number; y: number };
  /** Apply an explicit sort direction to this column, or clear it (null). */
  onSort: (dir: SortDir) => void;
  onClose: () => void;
  // ── Column management (AppDataGrid only; omit for custom-table grids) ──
  /** Hide the right-clicked column. Present ⇒ the "Hide Column" item renders. */
  onHideColumn?: () => void;
  /** False ⇒ "Hide Column" is disabled (it's the last visible column). */
  canHide?: boolean;
  /** Restore every hidden column. Present ⇒ the "Show All Columns" item renders. */
  onShowAllColumns?: () => void;
  /** False ⇒ "Show All Columns" is disabled (nothing is hidden). */
  hasHiddenColumns?: boolean;
  /** Open the Column Chooser popup. Present ⇒ the "Column Chooser…" item renders. */
  onColumnChooser?: () => void;
};

export function HeaderContextMenu({
  column, sort, position, onSort, onClose,
  onHideColumn, canHide = true, onShowAllColumns, hasHiddenColumns = false, onColumnChooser,
}: HeaderContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState(position);

  // Clamp inside the viewport (same rule as the cell menu / filter popups §18.6).
  // Depend on the primitive x/y — NOT the `position` object, which callers
  // recreate every render (e.g. a grid host that re-renders): with `[position]`
  // the effect re-ran (and re-read layout) on every parent render, leaving the
  // menu perpetually "not stable" for a click when its host re-renders often.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    let { x, y } = position;
    if (x + r.width > window.innerWidth - 8) x = Math.max(8, window.innerWidth - r.width - 8);
    if (y + r.height > window.innerHeight - 8) y = Math.max(8, window.innerHeight - r.height - 8);
    setPos({ x, y });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position.x, position.y]);

  // Close on outside click / Escape / scroll — standard transient-popup rules.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    // Capture phase + handled: Esc closes ONLY this menu. On the document it ran
    // after the hotkey store, whose Esc = the form's Cancel (closed the form
    // behind — Open Quote, 2026-10-20).
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); onClose(); } };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', onClose, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', onClose, true);
    };
  }, [onClose]);

  const isSorted = sort.key === column.field && sort.dir !== null;
  const asc  = isSorted && sort.dir === 'asc';
  const desc = isSorted && sort.dir === 'desc';

  return (
    <div ref={ref} className="cell-ctx-menu adg-hctx-menu" role="menu"
      style={{ left: pos.x, top: pos.y }}
      onContextMenu={e => e.preventDefault()}>
      {/* No column-name heading — the user right-clicked the column, so the title
          was redundant (client 2026-08-03: "no need right-click heading"). */}

      {/* ── 1. Sort ─────────────────────────────────────────────── */}
      <button type="button" role="menuitem" className="cell-ctx-item"
        disabled={!column.sortable}
        onClick={() => { onSort('asc'); onClose(); }}>
        <i className="ti ti-sort-ascending" /> Sort Ascending
        {asc && <i className="ti ti-check adg-hctx-check" />}
      </button>
      <button type="button" role="menuitem" className="cell-ctx-item"
        disabled={!column.sortable}
        onClick={() => { onSort('desc'); onClose(); }}>
        <i className="ti ti-sort-descending" /> Sort Descending
        {desc && <i className="ti ti-check adg-hctx-check" />}
      </button>
      <button type="button" role="menuitem" className="cell-ctx-item"
        disabled={!isSorted}
        onClick={() => { onSort(null); onClose(); }}>
        <i className="ti ti-arrows-sort" /> Clear Sort
      </button>

      {/* ── 2-4. Column management (only when the grid supplies the handlers) ── */}
      {(onHideColumn || onShowAllColumns || onColumnChooser) && (
        <>
          <div className="adg-hctx-sep" role="separator" />
          {onHideColumn && (
            <button type="button" role="menuitem" className="cell-ctx-item"
              disabled={!canHide}
              onClick={() => { onHideColumn(); onClose(); }}>
              <i className="ti ti-eye-off" /> Hide Column
            </button>
          )}
          {onShowAllColumns && (
            <button type="button" role="menuitem" className="cell-ctx-item"
              disabled={!hasHiddenColumns}
              onClick={() => { onShowAllColumns(); onClose(); }}>
              <i className="ti ti-eye" /> Show All Columns
            </button>
          )}
          {onColumnChooser && (
            <button type="button" role="menuitem" className="cell-ctx-item"
              onClick={() => { onColumnChooser(); onClose(); }}>
              <i className="ti ti-columns" /> Column Chooser…
            </button>
          )}
        </>
      )}
    </div>
  );
}
